<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import type { EditorToolbarItem, EditorSuggestionMenuItem, DropdownMenuItem } from '@nuxt/ui'
import { mapEditorItems } from '@nuxt/ui/utils/editor'
import { TextAlign } from '@tiptap/extension-text-align'
import type { JSONContent } from '@tiptap/core'

const props = defineProps<{ context?: Record<string, any> }>()

const contentType = computed<'html' | 'markdown' | 'json'>(() =>
  props.context?.attrs?.contentType ?? 'html'
)

const emptyDoc: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] }

function parseInitial(raw: any): any {
  if (!raw) return contentType.value === 'json' ? emptyDoc : ''
  if (contentType.value === 'json') {
    if (typeof raw === 'object') return raw
    try {
      return JSON.parse(raw)
    } catch {
      return emptyDoc
    }
  }
  return raw
}

const content = ref<any>(parseInitial(props.context?.value))
let debounce: ReturnType<typeof setTimeout> | null = null

watch(content, (val) => {
  if (debounce) clearTimeout(debounce)
  debounce = setTimeout(() => {
    if (!props.context?.node) return
    const out = contentType.value === 'json' && typeof val === 'object' ? JSON.stringify(val) : val
    props.context.node.input(out || null)
  }, 300)
})

function clearContent() {
  content.value = contentType.value === 'json' ? { ...emptyDoc } : ''
  props.context?.node?.input(null)
}

onMounted(() => {
  if (!props.context?.value) props.context?.node?.input(null)
})

// ── Itens de bloco reutilizados ───────────────────────────────────────────────
const blockItems = [
  { kind: 'paragraph', label: 'Parágrafo', icon: 'i-lucide-type' },
  { kind: 'heading', level: 1, label: 'Título 1', icon: 'i-lucide-heading-1' },
  { kind: 'heading', level: 2, label: 'Título 2', icon: 'i-lucide-heading-2' },
  { kind: 'heading', level: 3, label: 'Título 3', icon: 'i-lucide-heading-3' },
  { kind: 'bulletList', label: 'Lista com marcadores', icon: 'i-lucide-list' },
  { kind: 'orderedList', label: 'Lista numerada', icon: 'i-lucide-list-ordered' },
  { kind: 'blockquote', label: 'Citação', icon: 'i-lucide-text-quote' },
  { kind: 'codeBlock', label: 'Bloco de código', icon: 'i-lucide-square-code' }
]

const markItems: EditorToolbarItem[] = [
  { kind: 'mark', mark: 'bold', icon: 'i-lucide-bold', tooltip: { text: 'Negrito' } },
  { kind: 'mark', mark: 'italic', icon: 'i-lucide-italic', tooltip: { text: 'Itálico' } },
  { kind: 'mark', mark: 'underline', icon: 'i-lucide-underline', tooltip: { text: 'Sublinhado' } },
  { kind: 'mark', mark: 'strike', icon: 'i-lucide-strikethrough', tooltip: { text: 'Tachado' } },
  { kind: 'mark', mark: 'code', icon: 'i-lucide-code', tooltip: { text: 'Código' } }
]

const alignItems = [
  { kind: 'textAlign', align: 'left', icon: 'i-lucide-align-left', label: 'Esquerda' },
  { kind: 'textAlign', align: 'center', icon: 'i-lucide-align-center', label: 'Centro' },
  { kind: 'textAlign', align: 'right', icon: 'i-lucide-align-right', label: 'Direita' },
  { kind: 'textAlign', align: 'justify', icon: 'i-lucide-align-justify', label: 'Justificado' }
]

// ── Toolbar fixa ──────────────────────────────────────────────────────────────
const fixedToolbarItems: EditorToolbarItem[][] = [
  [
    { kind: 'undo', icon: 'i-lucide-undo', tooltip: { text: 'Desfazer' } },
    { kind: 'redo', icon: 'i-lucide-redo', tooltip: { text: 'Refazer' } }
  ], [
    { icon: 'i-lucide-heading', tooltip: { text: 'Títulos' }, content: { align: 'start' },
      items: blockItems.slice(1, 5).map(i => ({ ...i, tooltip: undefined })) },
    { icon: 'i-lucide-list', tooltip: { text: 'Listas' }, content: { align: 'start' },
      items: blockItems.slice(5, 7) },
    { kind: 'blockquote', icon: 'i-lucide-text-quote', tooltip: { text: 'Citação' } },
    { kind: 'codeBlock', icon: 'i-lucide-square-code', tooltip: { text: 'Bloco de código' } }
  ],
  markItems,
  [{ icon: 'i-lucide-align-justify', tooltip: { text: 'Alinhamento' }, content: { align: 'end' }, items: alignItems }],
  [{ kind: 'clearFormatting', icon: 'i-lucide-remove-formatting', tooltip: { text: 'Limpar formatação' } }]
]

// ── Bubble toolbar ────────────────────────────────────────────────────────────
const bubbleToolbarItems: EditorToolbarItem[][] = [
  [{
    label: 'Converter em', trailingIcon: 'i-lucide-chevron-down',
    activeColor: 'neutral', activeVariant: 'ghost',
    content: { align: 'start' }, ui: { label: 'text-xs' },
    items: [{ type: 'label', label: 'Converter em' }, ...blockItems]
  }],
  markItems,
  [{ icon: 'i-lucide-align-justify', tooltip: { text: 'Alinhamento' }, content: { align: 'end' }, items: alignItems }]
]

// ── Suggestion menu ───────────────────────────────────────────────────────────
const suggestionItems: EditorSuggestionMenuItem[][] = [
  [{ type: 'label', label: 'Estilo' }, ...blockItems.slice(1)],
  [
    { type: 'label', label: 'Inserir' },
    { kind: 'horizontalRule', label: 'Linha horizontal', icon: 'i-lucide-separator-horizontal' }
  ]
]

// ── Drag handle ───────────────────────────────────────────────────────────────
const selectedNode = ref<{ node: JSONContent, pos: number }>()

function handleItems(editor: Parameters<typeof mapEditorItems>[0]): DropdownMenuItem[][] {
  if (!selectedNode.value?.node?.type) return []
  const { node, pos } = selectedNode.value
  const label = node.type.charAt(0).toUpperCase() + node.type.slice(1)
  return mapEditorItems(editor, [
    [{ type: 'label', label },
      { label: 'Converter em', icon: 'i-lucide-repeat-2', children: blockItems },
      { kind: 'clearFormatting', pos, label: 'Limpar formatação', icon: 'i-lucide-rotate-ccw' }],
    [{ kind: 'duplicate', pos, label: 'Duplicar', icon: 'i-lucide-copy' }],
    [{ kind: 'moveUp', pos, label: 'Mover para cima', icon: 'i-lucide-arrow-up' },
      { kind: 'moveDown', pos, label: 'Mover para baixo', icon: 'i-lucide-arrow-down' }],
    [{ kind: 'delete', pos, label: 'Excluir', icon: 'i-lucide-trash' }]
  ]) as DropdownMenuItem[][]
}
</script>

<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
    <UEditor
      v-slot="{ editor, handlers }"
      v-model="content"
      :content-type="contentType"
      :extensions="[TextAlign.configure({ types: ['heading', 'paragraph'] })]"
      placeholder="Escreva ou digite '/' para comandos..."
      class="min-h-[200px]"
    >
      <UEditorToolbar
        :editor="editor"
        :items="fixedToolbarItems"
        class="border-b border-gray-200 dark:border-gray-700 sticky top-0 px-3 py-1.5 z-10 bg-white dark:bg-gray-900 overflow-x-auto no-print"
      >
        <template #trailing>
          <UButton
            icon="i-lucide-trash-2"
            size="xs"
            color="neutral"
            variant="ghost"
            title="Limpar conteúdo"
            class="ml-auto"
            @click="clearContent"
          />
        </template>
      </UEditorToolbar>

      <UEditorToolbar
        :editor="editor"
        :items="bubbleToolbarItems"
        layout="bubble"
        :should-show="({ view, state }) => view.hasFocus() && !state.selection.empty"
      />

      <UEditorSuggestionMenu
        :editor="editor"
        :items="suggestionItems"
      />

      <UEditorDragHandle
        v-slot="{ ui, onClick }"
        :editor="editor"
        @node-change="selectedNode = $event"
      >
        <UButton
          icon="i-lucide-plus"
          color="neutral"
          variant="ghost"
          size="sm"
          :class="ui.handle()"
          @click="(e) => { e.stopPropagation(); const s = onClick(); handlers.suggestion?.execute(editor, { pos: s?.pos }).run() }"
        />
        <UDropdownMenu
          v-slot="{ open }"
          :modal="false"
          :items="handleItems(editor)"
          :content="{ side: 'left' }"
          :ui="{ content: 'w-48', label: 'text-xs' }"
          @update:open="editor.chain().setMeta('lockDragHandle', $event).run()"
        >
          <UButton
            color="neutral"
            variant="ghost"
            active-variant="soft"
            size="sm"
            icon="i-lucide-grip-vertical"
            :active="open"
            :class="ui.handle()"
          />
        </UDropdownMenu>
      </UEditorDragHandle>
    </UEditor>
  </div>
</template>
